<template>
	<div>
		<div v-if="showListingFilter && !disableProductListingFilter" class="sm:container">
			<ProductListingFilter v-model="currentFilter" />
		</div>
		<div ref="productListRef" class="container">
			<div
				v-if="pagination && pagination.items.length > 0"
				class="transition-opacity space-y-4 md:space-y-8"
				:class="{'opacity-50': obsolete}"
			>
				<div
					v-for="(page, pageIndex) in pagination.pages"
					:key="page.page"
					class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-2 gap-4 md:gap-8"
				>
					<template v-for="(item, itemIndex) in injectAdsBetweenProducts(page.items, pageIndex)">
						<Click
							v-if="item.type === 'ad'"
							:key="`click-${itemIndex}`"
							:placement="item.placement"
							class="col-span-2"
						/>
						<ProductTile
							v-else
							:key="item.product.vanr || itemIndex"
							:product="item.product"
							:is-pending="pending || obsolete"
							:is-this-and-that-product="isRecommendationServiceUsed"
							@click="trackProduct([item.product], 'select_item')"
						/>
					</template>
				</div>

				<LazyProgress
					v-if="pagination.totalItems && !visibleOnlyShowMore"
					:items="pagination.totalItems"
					:now="(pagination.offset + pagination.items.length) / pagination.totalItems * 100"
					:thickness="12"
					aria-label="Postup v prohlížení produktů z naší akční nabídky"
				>
					<template #header>
						<div class="text-center mb-2">
							Viděli jste
							<span class="font-semibold">{{ Math.min(pagination.page * OFFER_PRODUCTS_PER_PAGE, pagination.totalItems) }}</span>
							z <span class="font-semibold">{{ pagination.totalItems }}</span> produktů v naší nabídce
						</div>
					</template>
				</LazyProgress>

				<div v-if="pagination.totalPages > 1 || pagination.hasMore" class="grid mt-8 md:[grid-template-columns:1fr_auto_1fr]">
					<button
						type="button"
						:disabled="pending || !pagination.hasMore"
						class="btn btn-primary btn-lg justify-self-center md:col-start-2"
						@click="next()"
					>
						{{ pending ? "Načítání..." : "Zobrazit další" }}
						<NuxtIcon
							name="arrow-right-circle"
							class="text-2xl rotate-90"
							filled
						/>
					</button>
					<LazyPaginationControls
						v-if="!visibleOnlyShowMore"
						:page="pagination.page"
						:total="pagination.totalPages"
						:scroll-to-target="productListRef"
						class="justify-self-end max-md:hidden"
						@prev="prev"
						@next="next(true)"
					/>
				</div>
			</div>
			<ProductListingSkeleton v-else-if="pending || pagination == null" />
			<div v-else class="mt-4 mb-16">
				<h4 class="text-body-primary">
					V této kategorii <strong>momentálně nemáme žádné produkty</strong> pro prodejnu {{ selectedHypermarketInstance?.name }}.
				</h4>
				<div v-if="fallbackListingSummary" class="mt-20 text-center">
					<span class="text-body-primary mb-4 block">
						V {{ fallbackListingSummary.nameInLocative }} je <strong>{{ fallbackListingSummary.totalCount }}</strong> produktů.
					</span>
					<AbsoluteInternalLink :to="fallbackListingSummary.link" class="btn btn-secondary btn-lg max-sm:w-full justify-center">
						<NuxtIcon name="custom/arrow-right" filled class="text-2xl text-icon-inverse" />
						{{ fallbackListingSummary.ctaLabel }}
					</AbsoluteInternalLink>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import {watchDebounced} from '@vueuse/core';
	import {storeToRefs} from 'pinia';
	import {isEqual} from 'lodash-es';
	import {AdPosition} from '../../openapi';
	import {OFFER_PRODUCTS_PER_PAGE, THISANDTHAT, MYGLOBUSPRICES, ROOT_PRODUCT_CATEGORY_ID, KONTOBARIERY} from '#shared/constants';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import type {ProductCatalogFilter} from '~/types/ProductCatalogFilter';
	import {useCustomerProfileStore} from '~/stores/customerProfile';
	import {useProductCatalogStore} from '~/stores/productCatalog';
	import ProductCatalogServiceFactory from '~/services/productCatalog/ProductCatalogServiceFactory';
	import useProductCatalogServiceSwitcher from '~/services/productCatalog/useProductCatalogServiceSwitcher';
	import {watchUntilRouteLeave} from '~/utils';
	import {ApiError, type Gsoa_ProductCatalog_ProductDetail as Product} from '~/openapi';

	interface Props {
		currentCategoryId?: string;
		actionOffer?: boolean;
		categoryName?: string | null;
	}

	type Emit = {
		(e: 'pending', value: boolean): void;
	};

	type GridItem = {
		type: 'product';
		product: Product;
	} | {
		type: 'ad';
		placement: AdPosition;
	};

	const props = defineProps<Props>();
	const emit = defineEmits<Emit>();

	const branchSpecificPath = useBranchSpecificPath();
	const productListRef = ref<HTMLElement | null>(null);
	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarketWithFallbackInstance, selectedHypermarketInstance} = storeToRefs(hypermarketsStore);

	const houseNumber = computed(
		() => selectedHypermarketWithFallbackInstance.value?.content.gsoaId,
	);

	const {name: routeName} = useRoute();
	const isActionOfferCategory = computed(() => routeName === 'branch-hypermarket-akcni-nabidka-category');
	const pageQuery = usePageRouteQuery();
	const countriesQuery = useVariadicRouteQuery('countries');
	const brandsQuery = useVariadicRouteQuery('brands');
	const category = useVariadicRouteParam('category');

	const disableProductListingFilter = computed(() =>
		category.value?.includes(KONTOBARIERY.slug)
		|| selectedHypermarketInstance.value?.branchType === 'fresh',
	);

	const productCatalogStore = useProductCatalogStore();
	const {actionCountries, countries, actionBrands, brands} = storeToRefs(productCatalogStore);

	const customerProfileStore = useCustomerProfileStore();
	const {userIsLogged} = storeToRefs(customerProfileStore);

	const maxSafeInteger = Number.MAX_SAFE_INTEGER;

	const breakpoints = useTailwindBreakpoints();
	const greaterXl = breakpoints.greaterOrEqual('xl');
	const greaterMd = breakpoints.greaterOrEqual('md');

	const advertisementCount = computed(() => {
		switch (true) {
			case greaterXl.value:
				return 4;
			case greaterMd.value:
				return 6;
			default:
				return 2;
		}
	});

	/*
	 * Advertisement positions for each page layout:
	 * xs,sm   md,lg     xl+
	 * [][]    [][][]    [][][AD]
	 * [][]    [AD][]    [][][][]
	 * [][]    [][][]    [AD][][]
	 * [][]    [][AD]    [][][][]
	 * [AD]    [][][]    [][][AD]
	 * [][]    [AD][]    [][][][]
	 * [][]    [][][]    [AD][][]
	 * [][]    [][AD]    [][][][]
	 * [][]    [][][]
	 * [][]    [AD][]
	 * [][]    [][][]
	 * [AD]    [][AD]
	 * [][]
	 * [][]
	 */
	const adIndices = {
		xs: [8, 21],
		md: [3, 9, 13, 19, 23, 29],
		xl: [2, 7, 16, 21],
	};

	const adPositions = [
		AdPosition.ACTION_OFFER_FIRST,
		AdPosition.ACTION_OFFER_SECOND,
		AdPosition.ACTION_OFFER_THIRD,
		AdPosition.ACTION_OFFER_FOURTH,
	];

	function injectAdsBetweenProducts(items: Product[], pageIndex: number): GridItem[] {
		const gridItems = items.map<GridItem>((product) => ({type: 'product', product}));

		if (pageIndex > 0) {
			return gridItems;
		}

		const injectIndices = greaterXl.value ? adIndices.xl : greaterMd.value ? adIndices.md : adIndices.xs;
		const adCount = Math.min(injectIndices.length, getAdCount(pageIndex));

		for (let adIndex = 0; adIndex < adCount; adIndex++) {
			const spliceIndex = injectIndices[adIndex];
			const placement = adPositions[adIndex] ?? adPositions[0];

			gridItems.splice(spliceIndex, 0, {type: 'ad', placement});
		}

		return gridItems;
	}

	function getAdCount(pageIndex: number): number {
		const pageItems = pagination?.value?.pages?.[pageIndex]?.items.length ?? 0;
		const maxItemsOnPage = OFFER_PRODUCTS_PER_PAGE / advertisementCount.value;

		return Math.floor(pageItems / maxItemsOnPage);
	}

	const currentFilter = ref<ProductCatalogFilter>({
		countries: countriesQuery.value ?? [],
		brands: brandsQuery.value ?? [],
	});

	function getListingFilterParam(selectedValues?: string[], totalAvailableValues = 0): string[] | undefined {
		const selectedValueCount = selectedValues?.length ?? 0;

		if (selectedValueCount === 0) {
			return undefined;
		}

		if (totalAvailableValues > 0 && selectedValueCount === totalAvailableValues) {
			return undefined;
		}

		return selectedValues;
	}

	const listingParams = computed(() => {
		if (disableProductListingFilter.value) {
			return {
				countries: undefined,
				brands: undefined,
				category: props.currentCategoryId,
			};
		}

		const totalCountryCount = (isActionOfferCategory.value ? actionCountries.value?.countries?.length : countries.value?.countries?.length) ?? 0;
		const totalBrandCount = (isActionOfferCategory.value ? actionBrands.value?.brands?.length : brands.value?.brands?.length) ?? 0;

		return {
			countries: getListingFilterParam(currentFilter.value.countries, totalCountryCount),
			brands: getListingFilterParam(currentFilter.value.brands, totalBrandCount),
			category: props.currentCategoryId,
		};
	});

	const hasActiveListingFilter = computed(() => (
		!!listingParams.value.countries?.length || !!listingParams.value.brands?.length
	));

	const productCatalogServiceFactory = computed(() => {
		switch (true) {
			case props.currentCategoryId === THISANDTHAT.id:
				return () => ProductCatalogServiceFactory.getRecommendationService({fallback: ProductCatalogServiceFactory.getMyGlobusBonusProgramPriceService});

			case props.currentCategoryId === MYGLOBUSPRICES.id:
				return userIsLogged.value
					? () => ProductCatalogServiceFactory.getRecommendationService({
						loadStrategy: 'bonus',
						fallback: ProductCatalogServiceFactory.getMyGlobusBonusProgramPriceService,
					})
					: ProductCatalogServiceFactory.getMyGlobusBonusProgramPriceService;

			case props.currentCategoryId === KONTOBARIERY.id:
				return ProductCatalogServiceFactory.getKontoBarieryService;

			case props.actionOffer:
				return ProductCatalogServiceFactory.getActionOfferService;

			case !props.actionOffer && props.currentCategoryId === ROOT_PRODUCT_CATEGORY_ID && !hasActiveListingFilter.value:
				return ProductCatalogServiceFactory.getFullOfferFilteredService;

			default:
				return ProductCatalogServiceFactory.getFullOfferService;
		}
	});

	const isRecommendationServiceUsed = computed(() => {
		return props.currentCategoryId === THISANDTHAT.id
			|| (props.currentCategoryId === MYGLOBUSPRICES.id && userIsLogged.value);
	});

	const serviceSwitcher = useProductCatalogServiceSwitcher(
		productCatalogServiceFactory,
	);

	const fallbackRootListingServiceFactory = computed(() => (
		props.actionOffer
			? ProductCatalogServiceFactory.getActionOfferService
			: ProductCatalogServiceFactory.getFullOfferFilteredService
	));

	const fallbackRootListingServiceSwitcher = useProductCatalogServiceSwitcher(
		fallbackRootListingServiceFactory,
	);

	type FallbackRootListingSummary = {
		totalCount?: number;
	} | null;

	const rootListingParams = computed(() => ({
		houseNumber: Number(houseNumber.value!),
		offset: 0,
		pageSize: 1,
		category: props.actionOffer ? undefined : ROOT_PRODUCT_CATEGORY_ID,
		countries: undefined,
		brands: undefined,
	}));

	// Each listing type needs its own key – during client navigation between action offer and full offer the new page
	// is set up while the old one is still mounted, so a shared key would reuse the old page's asyncData and handler
	const {pagination, next, prev, reset, pending, obsolete} = await usePagination(
		`ProductListing-${props.actionOffer ? 'actionOffer' : 'productCatalog'}`,
		() =>
			new Paginator(async (_signal, offset, pageSize) => {
				if (!houseNumber.value) {
					return {items: [], total: 0};
				}

				const productCatalogService = await serviceSwitcher.getService();

				if (!productCatalogService) {
					return null;
				}

				try {
					const response = await productCatalogService.getProducts({
						...listingParams.value,
						houseNumber: Number(houseNumber.value!),
						offset,
						pageSize,
					});

					trackProduct(response?.products ?? [], 'view_item_list');

					return {
						items: response?.products ?? [],
						// If GSOA returns 'paginationShowMore = true' but the total count is 0, set maxSafeInteger to display the "show more" button
						total: (response?.paginationShowMore ? ((response?.totalCount ?? 0) > 0 ? response.totalCount! : maxSafeInteger) : ((response?.totalCount ?? 0) > 0 && response?.paginationShowMore ? response.totalCount! : 0)) || 0,
					};
				} catch (error: unknown) {
					// Handle 404 specially - return empty list instead of showing error page
					if (error instanceof ApiError && error.status === 404) {
						return {items: [], total: 0};
					}
					// Other errors will be handled by the plugin (01.fixErrorStatusCode.ts)
					throw error;
				}
			}),
		{
			linear: true,
			pageSize: OFFER_PRODUCTS_PER_PAGE,
			initialPage: pageQuery.value,
		},
	);

	const fallbackRootListing = ref<FallbackRootListingSummary>(null);
	const fallbackRootListingCacheKey = ref<string | null>(null);
	const shouldFetchFallbackListingSummary = computed(() => (
		!!houseNumber.value
		&& !pending.value
		&& pagination.value?.items.length === 0
	));

	async function fetchFallbackRootListingSummary() {
		if (!shouldFetchFallbackListingSummary.value || !houseNumber.value) {
			fallbackRootListing.value = null;
			fallbackRootListingCacheKey.value = null;
			return;
		}

		const cacheKey = `${props.actionOffer ? 'action' : 'catalog'}-${houseNumber.value}`;

		if (fallbackRootListingCacheKey.value === cacheKey && fallbackRootListing.value) {
			return;
		}

		const productCatalogService = await fallbackRootListingServiceSwitcher.getService();

		if (!productCatalogService) {
			fallbackRootListing.value = null;
			fallbackRootListingCacheKey.value = null;
			return;
		}

		try {
			fallbackRootListing.value = await productCatalogService.getProducts(rootListingParams.value);
			fallbackRootListingCacheKey.value = cacheKey;
		} catch (error: unknown) {
			if (error instanceof ApiError && error.status === 404) {
				fallbackRootListing.value = null;
				fallbackRootListingCacheKey.value = cacheKey;
				return;
			}

			throw error;
		}
	}

	const visibleOnlyShowMore = computed(() => {
		const isSpecialCategory = [MYGLOBUSPRICES.id, THISANDTHAT.id].includes(props.currentCategoryId ?? '');
		const isMaxSafeIntegerWithMore = pagination.value?.totalItems === maxSafeInteger && pagination.value?.hasMore;
		return isSpecialCategory || isMaxSafeIntegerWithMore;
	});
	const showListingFilter = computed(() => pending.value || pagination.value == null || (pagination.value.items.length > 0));

	const fallbackListingSummary = computed(() => {
		const totalCount = fallbackRootListing.value?.totalCount;

		if (typeof totalCount !== 'number') {
			return null;
		}

		return {
			totalCount,
			nameInLocative: props.actionOffer ? 'akční nabídce' : 'celé nabídce',
			ctaLabel: props.actionOffer ? 'Zobrazit akční nabídku' : 'Zobrazit celou nabídku',
			link: branchSpecificPath({name: props.actionOffer ? 'hypermarket-akcni-nabidka-category' : 'hypermarket-cela-nabidka-category'}),
		};
	});

	watch(
		() => pagination?.value?.page,
		(newPage) => {
			const normalizedNewPage = newPage ?? 1;

			// Update if the new page is different from the current page
			if (normalizedNewPage !== pageQuery.value) {
				pageQuery.value = normalizedNewPage;
			}
		},
	);

	// Emit pending state when it changes
	watch(pending, (val) => {
		emit('pending', val);
	});

	// Reflect listing params in the URL query
	watchUntilRouteLeave(listingParams, (newParams) => {
		const newCountries = newParams.countries?.length ? newParams.countries : null;
		const newBrands = newParams.brands?.length ? newParams.brands : null;

		if (!isEqual(countriesQuery.value, newCountries)) {
			countriesQuery.value = newCountries;
		}
		if (!isEqual(brandsQuery.value, newBrands)) {
			brandsQuery.value = newBrands;
		}
	});

	// Refresh product list after any change to request params
	const listingRequestState = computed(() => ({
		brands: listingParams.value.brands ? [...listingParams.value.brands] : [],
		category: listingParams.value.category,
		countries: listingParams.value.countries ? [...listingParams.value.countries] : [],
		houseNumber: houseNumber.value,
		productCatalogServiceFactory: productCatalogServiceFactory.value,
	}));

	watchDebounced(listingRequestState, (newValues, oldValues) => {
		if (!isEqual(oldValues, newValues)) {
			reset();
		}
	}, {debounce: 100, flush: 'post'});

	watch([shouldFetchFallbackListingSummary, houseNumber, () => props.actionOffer], () => {
		fetchFallbackRootListingSummary();
	}, {immediate: true});

	function trackProduct(products: Product[], event: string) {
		trackEcommerceEvent(event, {
			item_list_id: category.value?.[0] ?? (props.actionOffer ? 'akcni_nabidka' : 'cela_nabidka'),
			item_list_name: !!props.categoryName && props.currentCategoryId !== ROOT_PRODUCT_CATEGORY_ID
				? props.categoryName
				: props.actionOffer
					? 'Akční nabídka'
					: 'Celá nabídka',
			items: mapProductsToGtmEvent(products),
		});
	}
</script>
